import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { isLink, logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import {
  EnumReviewAccount,
  type IReviewAccount,
  type IReviewAccountType,
} from 'entities/Review/api/types'
import { ReviewAccount } from 'entities/Review/model/ReviewAccount'
import { ReviewsApi } from 'entities/Review/api/review'
import { ReviewAddAccountsContent } from 'pages/reviews/ui/ReviewAccounts/ui/ReviewAddAccountsContent'
import { ReviewAccountContent } from 'pages/reviews/ui/ReviewAccounts/ui/ReviewAccountContent'

export class ReviewAccountsStore {
  private readonly _limit: number = 10
  private readonly _accounts: ReviewAccount[] = []
  private _modalId = nanoid()
  private _editedAccount: ReviewAccount | null = null
  private _errorsMap = new Map<string, string>()
  private _loading = false

  constructor(accountsResponse?: IReviewAccount[]) {
    if (accountsResponse) this.setAccounts(accountsResponse)

    makeAutoObservable(this)
  }

  get accounts() {
    return this._accounts
  }

  get accountsParams(): IReviewAccount[] {
    return this._accounts.map((account, index) => ({ ...account.params, sort_order: index }))
  }

  get isAddDisabled(): boolean {
    return this._accounts.length >= this._limit
  }

  get allowedAccounts() {
    return Object.keys(EnumReviewAccount)
      .filter((account): account is IReviewAccountType => account in EnumReviewAccount)
      .map(
        (account) =>
          new ReviewAccount({
            type: account,
            url_link: '',
            button_name: '',
          })
      )
  }

  get editedAccount() {
    return this._editedAccount
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isErrorExistInAccounts() {
    return this._accounts.some((account) => !!account.urlLinkError)
  }

  get loading() {
    return this._loading
  }

  private setAccounts(accountsResponse: IReviewAccount[]) {
    accountsResponse.forEach((account) => this._accounts.push(new ReviewAccount(account)))
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  onClose = () => {
    modalStore.removeModal(this._modalId)
    this._editedAccount = null
    this.errorsMap.clear()
  }

  showAddAccountsModal = () => {
    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Add accounts',
      showCloseButton: false,
      ModalContent: () => <ReviewAddAccountsContent store={this} />,
      onClose: this.onClose,
    })
  }

  showAddLinkModal = (account: ReviewAccount, isEdit = false) => {
    this._editedAccount = account

    modalStore.addModal({
      id: this._modalId,
      title: `${isEdit ? 'Edit' : 'Add'} link`,
      ModalContent: () => <ReviewAccountContent store={this} />,
      onClose: this.onClose,
      showCloseButton: false,
    })
  }

  onSaveLink = async (account: ReviewAccount, isEdit = false) => {
    try {
      if (this.loading || !this._editedAccount || !account) return

      this.errorsMap.clear()
      this.checkValidate(account)

      if (this.errorsMap.size) return

      runInAction(() => {
        this._loading = true
      })

      const params = account.params
      await ReviewsApi.validateReviewAccount(params)
      toastStore.add({ type: 'success', title: `Review account ${isEdit ? 'saved' : 'added'}` })
      this._editedAccount.syncOrigin(params)

      runInAction(() => {
        if (this._editedAccount) {
          const isExisting = this._accounts.includes(this._editedAccount)
          if (!isExisting) this._accounts.unshift(this._editedAccount)
        }
      })

      this.onClose()
    } catch (e) {
      logger.error(e)
      this.handleApiError(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  checkValidate(account: ReviewAccount) {
    if (!account) return

    const { url, buttonName } = account

    if (!url.trim().length || !isLink(url)) {
      this.setError('url_link', 'Enter a valid URL')
    }

    if (!buttonName.trim().length) {
      this.setError('button_name', 'Enter button name')
    }
  }

  reorderAccounts = (orderedAccounts: ReviewAccount[]) => {
    this._accounts.splice(0, this._accounts.length, ...orderedAccounts)
  }

  onEdit = (item: ReviewAccount) => {
    this.showAddLinkModal(item, true)
  }

  onRemove = (item: ReviewAccount) => {
    const index = this._accounts.indexOf(item)

    if (index !== -1) {
      this._accounts.splice(index, 1)
      toastStore.add({ type: 'success', title: 'Review account removed' })
    }
  }

  private handleApiError = (e: unknown) => {
    logger.error(e)

    if (e instanceof AxiosError) {
      e.errors.entries().forEach(([key, value]) => {
        if (key === 'button_name' || key === 'url_link') {
          this.setError(key, value.value)
        }
      })
    }
  }
}
